// oxlint-disable no-use-before-define
import React, { useCallback, useMemo, useState } from 'react';

import { AlineTransforms, Select } from '@aline/common';
import { CircularProgress } from '@mui/material';
import { Virtuoso } from 'react-virtuoso';
import {
  AlineButton,
  ConfirmationDialog,
  ConfirmationDialogVariant,
  PanelHeader,
  Spacer,
  ToggleSwitch,
} from 'src/components';
import { getLiveSpellcheckRange, isLintRangeIntact } from 'src/editor/spellcheck-helpers';
import { getSpellcheckKindColor } from 'src/editor/spellcheck-kind-colors';
import { useSpellcheckStateSelector } from 'src/editor/spellcheck-state-registry';
import type { SpellcheckLint, SpellcheckSuggestion } from 'src/editor/spellcheck-vars';
import { SPELLCHECK_STATUS } from 'src/editor/spellcheck-vars';
import { ClientTransforms } from 'src/editor/transforms';
import { useSpellcheckActions } from 'src/editor/use-spellcheck-actions';
import { useSpellcheckAvailable } from 'src/editor/use-spellcheck-available';
import { useSpellcheckEnabled } from 'src/editor/use-spellcheck-enabled';
import { useEditor, useEditorLoading } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

import { SuggestionPills, usePillStyles } from './SuggestionPills';
import { useBackToAI } from '../BackToAIButton';

/**
 * Without a fixed order the filter chips sort by first appearance in the document, so ignoring or
 * accepting a problem would reshuffle them. Unknown kinds sort after these, alphabetically.
 */
const KIND_ORDER = ['Spelling', 'Typo', 'Word Choice', 'Style', 'Repetition', 'Miscellaneous'];

const useStyles = makeStyles()(theme => ({
  card: {
    background: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 10,
    boxShadow: theme.appShadows.banner,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: theme.spacing(1.25, 1.5),
  },
  cardHeader: {
    alignItems: 'center',
    display: 'flex',
    fontSize: 12.5,
    fontWeight: 600,
    gap: 8,
  },
  dot: {
    borderRadius: '50%',
    flexShrink: 0,
    height: 9,
    width: 9,
  },
  empty: {
    color: theme.appColors.secondaryType,
    fontSize: 13,
    padding: theme.spacing(3, 2),
    textAlign: 'center',
  },
  ignoreAll: { flexShrink: 0 },
  ignoreAllLabel: {
    fontSize: 12,
    fontWeight: 600,
    height: 22,
    minWidth: 0,
    padding: theme.spacing(0, 0.75),
  },
  kindChip: {
    alignItems: 'center',
    background: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 999,
    color: theme.appColors.secondaryType,
    cursor: 'pointer',
    display: 'flex',
    fontSize: 11,
    fontWeight: 500,
    gap: 5,
    maxWidth: '100%',
    minWidth: 0,
    padding: theme.spacing(0.5, 1),
  },
  kindChipLabel: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  kindCount: { color: theme.appColors.placeholderType, fontVariantNumeric: 'tabular-nums' },
  kindSummary: {
    borderBottom: `1px solid ${theme.appColors.ruleSubtle}`,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    padding: theme.spacing(1.5, 1.5, 1.5),
  },
  list: {
    flex: 1,
    minHeight: 0,
  },
  listItem: {
    padding: theme.spacing(0, 1.5, 1),
  },
  message: { color: theme.appColors.strongType, fontSize: 13, fontWeight: 500, lineHeight: 1.4 },
  pillRow: { alignItems: 'center', display: 'flex', gap: 6, justifyContent: 'flex-end' },
  pills: { display: 'flex', flex: 1, flexWrap: 'wrap', gap: 6 },
  root: {
    background: theme.appColors.background,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    // The drawer content wrapper scrolls, so unset it here and let only the virtualized list scroll.
    overflowY: 'unset',
    width: '100%',
  },
  snippet: {
    background: theme.appColors.background,
    border: `1px solid ${theme.appColors.ruleSubtle}`,
    borderRadius: 7,
    color: theme.appColors.secondaryType,
    cursor: 'pointer',
    fontSize: 12,
    lineHeight: 1.45,
    padding: theme.spacing(1, 1.5),
    textAlign: 'left',
    width: '100%',
  },
  snippetMark: { borderRadius: 3, fontWeight: 600, padding: '0 2px' },
  status: { color: theme.appColors.secondaryType, fontSize: 12 },
  statusBusy: { alignItems: 'center', display: 'flex', gap: 6 },
  statusRow: {
    alignItems: 'center',
    columnGap: 8,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    minHeight: 30,
    padding: theme.spacing(1, 1, 0, 2),
    rowGap: 2,
  },
}));

type SpellcheckDrawerClasses = ReturnType<typeof useStyles>['classes'];

export function SpellcheckDrawer(): JSX.Element {
  const { classes } = useStyles();
  const handleBackToAI = useBackToAI();
  const editor = useEditor();
  const { acceptLint, canReplace, ignoreLints } = useSpellcheckActions();

  const { enabled: userEnabled, loading: documentLoading, setEnabled } = useSpellcheckEnabled();
  const available = useSpellcheckAvailable();
  const editorLoading = useEditorLoading();
  /**
   * The stored setting defaults to on until the document answers, so while it is loading neither
   * state is known. Claiming either one shows the reader a toggle that then flips under them.
   */
  const enabled = userEnabled && available && !documentLoading;
  const checking = useSpellcheckStateSelector(state => state.checking);
  const lints = useSpellcheckStateSelector(state => state.lints);
  const status = useSpellcheckStateSelector(state => state.status);

  const loading = status === SPELLCHECK_STATUS.loading;

  const [kindFilter, setKindFilter] = useState<string | null>(null);
  const [ignoreAllKind, setIgnoreAllKind] = useState<string | null>(null);
  const [confirmingIgnoreAll, setConfirmingIgnoreAll] = useState(false);

  const kindCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const lint of lints) counts.set(lint.kind, (counts.get(lint.kind) ?? 0) + 1);
    return [...counts.entries()].sort(([a], [b]) => compareKinds(a, b));
  }, [lints]);

  const activeKind = kindCounts.some(([kind]) => kind === kindFilter) ? kindFilter : null;

  // Unset the kind filter if there are no lints of that type
  if (kindFilter && !activeKind) setKindFilter(null);

  const lintsOfKind = useCallback(
    (kind: string | null) => (kind ? lints.filter(lint => lint.kind === kind) : lints),
    [lints]
  );

  const visibleLints = useMemo(() => lintsOfKind(activeKind), [activeKind, lintsOfKind]);

  const ignoreAllLints = useMemo(() => lintsOfKind(ignoreAllKind), [ignoreAllKind, lintsOfKind]);

  const focusLint = useCallback(
    (lint: SpellcheckLint) => {
      const range = getLiveSpellcheckRange(lint);
      if (!range || !isLintRangeIntact(editor, lint)) return;
      AlineTransforms.select(editor, range);
      ClientTransforms.focus(editor);
    },
    [editor]
  );

  const ignoreLint = useCallback((lint: SpellcheckLint) => ignoreLints([lint]), [ignoreLints]);

  const openIgnoreAll = useCallback(() => {
    setIgnoreAllKind(activeKind);
    setConfirmingIgnoreAll(true);
  }, [activeKind]);

  const ignoreAll = useCallback(() => ignoreLints(ignoreAllLints), [ignoreAllLints, ignoreLints]);

  return (
    <div className={classes.root} data-cy={Select.document.editor.rightDrawer.spellcheck.container}>
      <PanelHeader
        showBackButton
        backButtonTooltip="Back to AI"
        text="Spellcheck"
        onClickBack={handleBackToAI}
      >
        <Spacer />
        <ToggleSwitch
          aria-checked={enabled}
          aria-label="Spellcheck"
          disabled={!available || documentLoading}
          role="switch"
          toggle={() => setEnabled(!enabled)}
          toggled={enabled}
        />
      </PanelHeader>

      {!available && !editorLoading && (
        <div className={classes.empty}>Spellchecking is only available while editing.</div>
      )}
      {available && !enabled && !documentLoading && (
        <div className={classes.empty}>Spellchecker is off.</div>
      )}

      {enabled && (
        <>
          <div className={classes.statusRow}>
            <div
              className={classes.status}
              data-cy={Select.document.editor.rightDrawer.spellcheck.status}
            >
              <SpellcheckStatus
                checking={checking}
                classes={classes}
                lintCount={lints.length}
                loading={loading}
                status={status}
              />
            </div>
            {!loading && visibleLints.length > 0 && (
              <AlineButton
                buttonClassName={classes.ignoreAllLabel}
                className={classes.ignoreAll}
                color="primary"
                size="small"
                variant="text"
                onClick={openIgnoreAll}
              >
                Ignore All ({visibleLints.length})
              </AlineButton>
            )}
          </div>
          {!loading && (
            <SpellcheckProblems
              activeKind={activeKind}
              canReplace={canReplace}
              checking={checking}
              classes={classes}
              hasLints={lints.length > 0}
              kindCounts={kindCounts}
              lints={visibleLints}
              onAccept={acceptLint}
              onFilterKind={setKindFilter}
              onFocus={focusLint}
              onIgnore={ignoreLint}
            />
          )}
          <ConfirmationDialog
            message={getIgnoreAllMessage(ignoreAllKind, ignoreAllLints.length)}
            open={confirmingIgnoreAll}
            primaryText="Ignore All"
            title="Ignore all problems"
            variant={ConfirmationDialogVariant.Warn}
            onClickPrimary={ignoreAll}
            onClose={() => setConfirmingIgnoreAll(false)}
          />
        </>
      )}
    </div>
  );
}

function getIgnoreAllMessage(activeKind: string | null, count: number): string {
  const problems = `${count} ${activeKind ? `${activeKind} ` : ''}problem${count === 1 ? '' : 's'}`;
  return `Ignore ${problems}? They stop being reported in this document for you, which cannot be undone.`;
}

function compareKinds(a: string, b: string): number {
  const indexA = KIND_ORDER.indexOf(a);
  const indexB = KIND_ORDER.indexOf(b);
  if (indexA !== -1 && indexB !== -1) return indexA - indexB;
  if (indexA !== -1) return -1;
  if (indexB !== -1) return 1;
  return a.localeCompare(b);
}

interface SpellcheckStatusProps {
  checking: boolean;
  classes: SpellcheckDrawerClasses;
  lintCount: number;
  loading: boolean;
  status: string;
}

function SpellcheckStatus({
  checking,
  classes,
  lintCount,
  loading,
  status,
}: SpellcheckStatusProps): JSX.Element {
  if (loading || checking)
    return (
      <span className={classes.statusBusy}>
        <CircularProgress size={12} thickness={5} />
        {loading ? 'Loading spellchecker…' : 'Checking…'}
      </span>
    );

  return (
    <>
      {status} · {lintCount} problem{lintCount === 1 ? '' : 's'}
    </>
  );
}

interface SpellcheckProblemsProps {
  activeKind: string | null;
  canReplace: boolean;
  checking: boolean;
  classes: SpellcheckDrawerClasses;
  hasLints: boolean;
  kindCounts: [string, number][];
  lints: SpellcheckLint[];
  onAccept: (lint: SpellcheckLint, suggestion: SpellcheckSuggestion) => void;
  onFilterKind: (kind: string | null) => void;
  onFocus: (lint: SpellcheckLint) => void;
  onIgnore: (lint: SpellcheckLint) => void;
}

function SpellcheckProblems({
  activeKind,
  canReplace,
  checking,
  classes,
  hasLints,
  kindCounts,
  lints,
  onAccept,
  onFilterKind,
  onFocus,
  onIgnore,
}: SpellcheckProblemsProps): JSX.Element {
  return (
    <>
      {kindCounts.length > 0 && (
        <div aria-label="Filter problems by kind" className={classes.kindSummary}>
          {kindCounts.map(([kind, count]) => {
            const color = getSpellcheckKindColor(kind);
            const selected = activeKind === kind;
            return (
              <button
                key={kind}
                aria-pressed={selected}
                className={classes.kindChip}
                style={
                  selected
                    ? { background: color.tint, borderColor: color.dot, color: color.pillText }
                    : undefined
                }
                title={selected ? 'Show all problems' : `Show only ${kind}`}
                type="button"
                onClick={() => onFilterKind(selected ? null : kind)}
              >
                <span
                  className={classes.dot}
                  style={{ background: color.dot, height: 8, width: 8 }}
                />
                <span className={classes.kindChipLabel}>{kind}</span>
                <span
                  className={classes.kindCount}
                  style={selected ? { color: 'inherit' } : undefined}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {!hasLints && !checking && <div className={classes.empty}>No problems found.</div>}

      {hasLints && (
        <Virtuoso
          className={classes.list}
          components={{ Footer: ListBottomSpacer, Header: ListTopSpacer }}
          itemContent={(index: number): JSX.Element => (
            <div className={classes.listItem}>
              <LintCard
                canReplace={canReplace}
                classes={classes}
                lint={lints[index]}
                onAccept={onAccept}
                onFocus={onFocus}
                onIgnore={onIgnore}
              />
            </div>
          )}
          totalCount={lints.length}
        />
      )}
    </>
  );
}

interface LintCardProps {
  canReplace: boolean;
  classes: SpellcheckDrawerClasses;
  lint: SpellcheckLint;
  onAccept: (lint: SpellcheckLint, suggestion: SpellcheckSuggestion) => void;
  onFocus: (lint: SpellcheckLint) => void;
  onIgnore: (lint: SpellcheckLint) => void;
}

const LintCard = React.memo(function LintCard({
  canReplace,
  classes,
  lint,
  onAccept,
  onFocus,
  onIgnore,
}: LintCardProps): JSX.Element {
  const { classes: pillClasses } = usePillStyles();
  const color = getSpellcheckKindColor(lint.kind);

  return (
    <div
      className={classes.card}
      data-cy={Select.document.editor.rightDrawer.spellcheck.problemRow}
    >
      <div className={classes.cardHeader}>
        <span className={classes.dot} style={{ background: color.dot }} />
        <span>{lint.kind}</span>
      </div>
      <div className={classes.message}>{lint.message}</div>
      <button
        aria-label="Focus problem in editor"
        className={classes.snippet}
        type="button"
        onClick={() => onFocus(lint)}
      >
        {lint.contextBefore ? `…${lint.contextBefore}` : ''}
        <mark
          className={classes.snippetMark}
          style={{ background: color.tint, color: color.pillText }}
        >
          {lint.problem}
        </mark>
        {lint.contextAfter ? `${lint.contextAfter}…` : ''}
      </button>
      <div className={classes.pillRow}>
        <div className={classes.pills}>
          <SuggestionPills canReplace={canReplace} lint={lint} onAccept={onAccept} />
        </div>
        <AlineButton
          buttonClassName={pillClasses.pill}
          color="primary"
          size="small"
          variant="text"
          onClick={() => onIgnore(lint)}
        >
          Ignore
        </AlineButton>
      </div>
    </div>
  );
});

function ListTopSpacer(): JSX.Element {
  return <div style={{ height: 12 }} />;
}

function ListBottomSpacer(): JSX.Element {
  return <div style={{ height: 16 }} />;
}
